import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

import { THEMES, DEFAULT_THEME as DEFAULT_THEME_DEF } from "@/theme/themes";
import type { ThemeName } from "@/theme/themes";

// UI preferences that should survive a reload — sidebar collapsed state,
// the colour theme, which view Home opens on and the audio players' speed.
// Persisted to localStorage; a fresh profile gets the defaults.

// Theme identity is sourced from the theme registry (src/theme/themes).
// The active name is written to <html data-theme> (see applyTheme) and
// read back before first paint by the inline script in index.html, so
// there's no flash of the wrong theme on load. Add a theme by editing
// the registry — it flows through here automatically.
export type { ThemeName };

export const THEME_NAMES: readonly string[] = THEMES.map((t) => t.name);

export const DEFAULT_THEME: ThemeName = DEFAULT_THEME_DEF.name;

/**
 * Home's view: one agent in full ("agent", the default: its card, the org
 * chart, its files) or the whole team at a glance ("team": what needs you,
 * who is working, everyone's activity, the agents, deliverables).
 */
export type HomeView = "agent" | "team";

/** The speeds the audio players offer (components/ui/AudioPlayer), remembered for every player. */
export const PLAYBACK_RATES = [1, 1.25, 1.5, 2] as const;
export type PlaybackRate = (typeof PLAYBACK_RATES)[number];

function playbackRateOf(value: unknown): PlaybackRate {
  return (PLAYBACK_RATES as readonly number[]).includes(value as number) ? (value as PlaybackRate) : 1;
}

interface UiState {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  homeView: HomeView;
  setHomeView: (view: HomeView) => void;
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  toggleTheme: () => void;
  playbackRate: PlaybackRate;
  setPlaybackRate: (rate: PlaybackRate) => void;
}

export const useUi = create<UiState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      toggleSidebar: () =>
        set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      homeView: "agent",
      setHomeView: (homeView: HomeView) => set({ homeView }),
      theme: DEFAULT_THEME,
      setTheme: (theme: ThemeName) => set({ theme }),
      // Cycle through every registered theme (not just two).
      toggleTheme: () =>
        set((s) => {
          const i = THEME_NAMES.indexOf(s.theme);
          return { theme: THEME_NAMES[(i + 1) % THEME_NAMES.length] };
        }),
      playbackRate: 1,
      setPlaybackRate: (rate: PlaybackRate) => set({ playbackRate: playbackRateOf(rate) }),
    }),
    {
      name: "corvus:ui",
      storage: createJSONStorage(() => localStorage),
      // v1: "mono" was renamed to "midnight" — map old saved prefs forward
      // so a returning user keeps their dark theme instead of snapping to
      // the default. v2: "pulse" was removed — it (and any other name the
      // registry no longer has) falls back to the default theme. The
      // no-flash script in index.html does the same pre-React. v3: Home's
      // views became Agent (the default) and Team. The old "board" was a
      // choice and becomes Agent; the old "overview" was saved for every
      // browser whether or not anyone picked it, so it cannot be told from
      // a choice and becomes the new default too. A choice made since wins.
      version: 3,
      migrate: (persisted, version) => {
        const s = persisted as Partial<UiState> | undefined;
        if (s) {
          if ((s.theme as string) === "mono") s.theme = "midnight";
          if (!s.theme || !THEME_NAMES.includes(s.theme)) s.theme = DEFAULT_THEME;
          if (version < 3) s.homeView = "agent";
        }
        return s as UiState;
      },
      // Self-heal on every load (no version bump needed for a future theme
      // removal): a persisted name the registry doesn't know becomes the
      // default, so Settings → Appearance always shows an active card.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<UiState>;
        return {
          ...current,
          ...p,
          theme:
            p.theme && THEME_NAMES.includes(p.theme) ? p.theme : DEFAULT_THEME,
          homeView: p.homeView === "team" ? "team" : "agent",
          playbackRate: playbackRateOf(p.playbackRate),
        };
      },
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
        homeView: state.homeView,
        playbackRate: state.playbackRate,
      }),
    },
  ),
);
